import { React, useEffect, useState } from "react";
import {
  Users,
  Phone,
  CheckCircle,
  PhoneCall,
  Briefcase,
  PhoneOff,
  Clock,
  ActivityIcon,
} from "lucide-react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import PhoneVerificationDrawer from "../../Leads/Modals/PhoneVerificationDrawer";
import {
  GetRecruiterDashboardSummary,
  GetRecruiterDashboardAnalytics,
} from "../Services/DashboardService";
import { timeAgoU, getInitialsU } from "../../../../Utils/CosmeticUtils";

const Dashboard = () => {
  const statusColors = {
    "CV Sent": "#BAE6FD", // lightest sky blue
    Shortlisted: "#7DD3FC", // soft sky
    "Interview Sheduled": "#38BDF8", // your chart line color
    Selected: "#1D4ED8", // deep blue
    BGV: "#2DD4BF", // teal-green
    "Offer Released": "#FCD34D", // warm amber/gold
    Hired: "#4ADE80", // fresh green
    Rejected: "#CBD5E1", // light gray
    Backout: "#94A3B8", // cool gray
  };
  const [open, setOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendRange, setTrendRange] = useState(7);
  const [hoveredSlice, setHoveredSlice] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const [summaryRes, analyticsRes] = await Promise.all([
        GetRecruiterDashboardSummary(),
        GetRecruiterDashboardAnalytics(),
      ]);

      setDashboard({
        ...summaryRes?.dashboard,
        ...analyticsRes?.dashboard,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const activityTrendData = (dashboard?.activityTrend || []).map((item) => ({
    date: item.activityDate,
    label: new Date(item.activityDate).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    }),
    activities: item.totalActivities,
  }));

  const filteredTrendData = activityTrendData.slice(-trendRange);

  const pipelineData = (dashboard?.pipeline || []).map((item) => ({
    name: item.status,
    value: item.count,
    color: statusColors[item.status] || "#F59E0B",
  }));
  const pipelineTotal = pipelineData.reduce((sum, item) => sum + item.value, 0);
  const hoveredPct = hoveredSlice
    ? ((hoveredSlice.value / pipelineTotal) * 100).toFixed(0)
    : null;
  const upcomingInterviewData = dashboard?.upcomingInterviews || [];

  const recentActivityData = dashboard?.recentCalls || [];

  const expectedJoiningData = dashboard?.expectedJoinings || [];

  return (
    <>
      <div className="p-6 pt-1  min-h-screen">
        {/* HEADER */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              Recruiter Dashboard
            </h1>

            <p className="text-sm text-gray-500">
              Welcome back! Here's your recruitment activity overview.
            </p>
          </div>

          {/* ADD LEAD BUTTON */}
          <button
            onClick={() => setOpen(true)}
            className=" flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl font-medium shadow-sm transition duration-200 ease-out transform-gpu hover:bg-primary hover:shadow-md hover:shadow-primary/30 hover:scale-[1.02]"
          >
            + Add Lead
          </button>
        </div>

        {/*  CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          <Card
            title="Assigned Leads"
            value={dashboard?.cards?.assignedLeads}
            icon={<Users />}
          />

          <Card
            title="Activities Today"
            value={dashboard?.cards?.activitiesToday}
            icon={<Phone />}
          />

          <Card
            title="Connected Today"
            value={dashboard?.cards?.connectedToday}
            icon={<PhoneCall />}
          />

          <Card
            title="Active Deals"
            value={dashboard?.cards?.activeDeals}
            icon={<Briefcase />}
          />

          <Card
            title="Placements This Month"
            value={dashboard?.cards?.placementsThisMonth}
            icon={<CheckCircle />}
          />
        </div>

        {/* CHARTS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* DAILY CALLING TREND CHART */}
          <div className="bg-white p-6 rounded-xl shadow col-span-2">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Daily Calling Trend
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {filteredTrendData?.length > 0 && (
                    <>
                      {new Date(filteredTrendData[0].date).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                        },
                      )}
                      {" to "}
                      {new Date(
                        filteredTrendData[filteredTrendData.length - 1].date,
                      ).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </>
                  )}
                </p>
              </div>

              {/* RANGE SWITCHER */}
              <div className="inline-flex bg-gray-100 rounded-lg p-1 gap-1">
                {[7, 15, 30].map((days) => (
                  <button
                    key={days}
                    onClick={() => setTrendRange(days)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      trendRange === days
                        ? "bg-white text-blue-600 shadow-sm border border-blue-200"
                        : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>

            {/* CHART */}
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={filteredTrendData}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38BDF8" stopOpacity={0.6} />{" "}
                    {/* was #3B82F6 / 0.25 */}
                    <stop
                      offset="95%"
                      stopColor="#DFF4FF"
                      stopOpacity={0.15}
                    />{" "}
                    {/* was #3B82F6 / 0 */}
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#E5E7EB"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  stroke="#c4c7cc"
                  style={{ fontSize: "12px" }}
                />
                <YAxis stroke="#c4c7cc" style={{ fontSize: "12px" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1F2937",
                    border: "1px solid #4B5563",
                    borderRadius: "6px",
                    color: "#fff",
                    padding: "10px",
                  }}
                  formatter={(value) => [`${value} calls`, "Activities"]}
                />
                <Area
                  type="monotone"
                  dataKey="activities"
                  stroke="#38BDF8"
                  strokeWidth={2.5}
                  fill="url(#colorTrend)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* PIE CHART */}
          <div className="bg-white p-5 rounded-xl shadow">
            <h2 className="text-lg font-semibold mb-4">
              My Recruitment Pipeline
            </h2>

            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pipelineData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  dataKey="value"
                  paddingAngle={2}
                  onMouseEnter={(data) => setHoveredSlice(data)}
                  onMouseLeave={() => setHoveredSlice(null)}
                >
                  {pipelineData.map((entry, index) => {
                    const isHovered = hoveredSlice?.name === entry.name;
                    return (
                      <Cell
                        key={index}
                        fill={entry.color}
                        stroke="none"
                        outerRadius={isHovered ? 110 : 100}
                        opacity={hoveredSlice && !isHovered ? 0.4 : 1}
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      />
                    );
                  })}
                </Pie>

                {/* CENTER — big % */}
                <text
                  x="37%"
                  y="49%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ fontSize: "26px", fontWeight: 700, fill: "#111827" }}
                >
                  {hoveredSlice
                    ? `${((hoveredSlice.value / pipelineData.reduce((a, b) => a + b.value, 0)) * 100).toFixed(0)}%`
                    : "100%"}
                </text>

                {/* CENTER — label below % */}
                <text
                  x="36%"
                  y="57%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{
                    fontSize: "10.1px",
                    fontWeight: 600,
                    fill: "#38BDF8",
                    letterSpacing: "0.08em",
                  }}
                >
                  {hoveredSlice ? hoveredSlice.name?.toUpperCase() : "PIPELINE"}
                </text>
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  formatter={(value, entry) => (
                    <span
                      style={{
                        fontSize: "12px",
                        color:
                          hoveredSlice?.name === value ? "#111827" : "#6B7280",
                        fontWeight: hoveredSlice?.name === value ? 600 : 400,
                        transition: "all 0.2s ease",
                      }}
                    >
                      {value}{" "}
                      <span style={{ fontWeight: 600, color: "#111827" }}>
                        ({entry.payload.value})
                      </span>
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              {hoveredSlice ? (
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-800">
                    {hoveredSlice.name}
                  </span>
                  <span className="text-gray-600">
                    {hoveredSlice.value} ({hoveredPct}%)
                  </span>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  Hover a slice to see details
                </p>
              )}
            </div>
          </div>
        </div>

        {/* TABLE + ACTIVITY */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* TABLE */}
          <div className="bg-white rounded-xl shadow col-span-2 h-[385px] flex flex-col">
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">Upcoming Interviews</h2>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-6 py-3 text-left pl-[60px]">Candidate</th>
                    <th className="px-6 py-3 text-left">Phone</th>
                    <th className="px-6 py-3 text-left">Company</th>
                    <th className="px-6 py-3 text-left">Role</th>
                    <th className="px-6 py-3 text-left">Interview Date</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {upcomingInterviewData.length > 0 ? (
                    upcomingInterviewData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        {/* CONTACT */}
                        <td className="px-6 py-4 flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-semibold">
                            {item.candidateName
                              ?.split(" ")
                              .map((word) => word[0])
                              .join("")
                              .substring(0, 2)}
                          </div>
                          <span className="font-medium text-gray-800">
                            {item.candidateName}
                          </span>
                        </td>

                        {/* PHONE NO */}
                        <td className="px-6 py-4 text-gray-600">
                          {item.phoneNo}
                        </td>
                        {/* COMPANY */}
                        <td className="px-6 py-4 text-gray-600">
                          {item.company}
                        </td>
                        {/* ROLE */}
                        <td className="px-6 py-4 text-gray-600">{item.role}</td>

                        {/* INTERVIEW DATE */}
                        <td className="px-6 py-4 text-gray-600">
                          {new Date(item.interviewDate).toLocaleString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-6 py-8 text-center text-gray-500"
                      >
                        No upcoming interviews scheduled.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ACTIVITY */}
          <div className="bg-white rounded-xl shadow h-[385px] flex flex-col">
            <div className="px-4 py-4 border-b flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-1.5">
                <ActivityIcon size={13} className="text-gray-400" />
                Recent activity
              </h2>
              <span className="text-[13px] text-gray-400 bg-gray-50 border border-gray-100 rounded-full px-2 py-0.5">
                {recentActivityData.length} calls
              </span>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-gray-50">
              {recentActivityData.length > 0 ? (
                recentActivityData.map((item, index) => {
                  const conn =
                    item.callResponse?.trim().toUpperCase() === "CONNECTED";

                  return (
                    <div
                      key={index}
                      className="flex items-center gap-2.5 px-4 py-2 hover:bg-gray-50 transition-colors"
                    >
                      {/* AVATAR */}
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0 ${conn ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700"}`}
                      >
                        {getInitialsU(item.candidateName)}
                      </div>

                      {/* CONTENT */}
                      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                        {/* LINE 1 — name + badge */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-[12.5px] font-medium text-gray-800 truncate">
                            {item.candidateName}
                          </span>
                          <span
                            className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0 ${conn ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700"}`}
                          >
                            {conn ? (
                              <PhoneCall size={8} />
                            ) : (
                              <PhoneOff size={8} />
                            )}
                            {conn ? "Connected" : "Not connected"}
                          </span>
                        </div>

                        {/* LINE 2 — org · role | remark | timestamp */}
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-[11px] text-gray-500 truncate">
                            {item.targetOrganization} · {item.targetRole}
                          </span>
                          {item.remark && (
                            <>
                              <span className="text-gray-300 flex-shrink-0">
                                ·
                              </span>
                              <span className="text-[11px] text-gray-400 italic truncate flex-1 min-w-0">
                                "{item.remark}"
                              </span>
                            </>
                          )}
                          <span className="text-[10px] text-gray-400 flex-shrink-0 ml-auto">
                            {timeAgoU(item.actionDate)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-gray-400 text-sm py-10">
                  No recent activities found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* EXPECTED JOININGS */}

        <div className="bg-white rounded-xl shadow overflow-hidden mt-6">
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold">Expected Joinings</h2>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-6 py-3 text-left">Candidate</th>
                <th className="px-6 py-3 text-left">Phone</th>
                <th className="px-6 py-3 text-left">Company</th>
                <th className="px-6 py-3 text-left">Role</th>
                <th className="px-6 py-3 text-left">Joining Date</th>
                <th className="px-6 py-3 text-left">CTC</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {expectedJoiningData.length > 0 ? (
                expectedJoiningData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {/* CANDIDATE */}
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {item.candidateName}
                    </td>

                    {/* PHONE */}
                    <td className="px-6 py-4 text-gray-600">{item.phoneNo}</td>

                    {/* COMPANY */}
                    <td className="px-6 py-4 text-gray-600">{item.company}</td>

                    {/* ROLE */}
                    <td className="px-6 py-4 text-gray-600">{item.role}</td>

                    {/* JOINING DATE */}
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(item.expectedJoiningDate).toLocaleDateString(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </td>

                    {/* CTC */}
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                        ₹
                        {item.offeredCTC
                          ? (item.offeredCTC / 100000).toFixed(1) + " LPA"
                          : "-"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No expected joinings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <PhoneVerificationDrawer open={open} onClose={() => setOpen(false)} />
    </>
  );
};

/* COMPONENTS */

const Card = ({ title, value, icon, red }) => (
  <div className="bg-white rounded-xl shadow p-5 flex justify-between items-center">
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <h2 className="text-2xl font-bold">{value}</h2>
    </div>
    <div
      className={`p-3 rounded-lg ${
        red ? "bg-red-100 text-red-500" : "bg-sky-100 text-sky-500"
      }`}
    >
      {icon}
    </div>
  </div>
);

export default Dashboard;
